type FaceApiDetection = {
  descriptor: Float32Array;
  detection?: { score?: number };
};

type FaceApi = {
  nets: {
    tinyFaceDetector: { isLoaded: boolean; loadFromUri: (uri: string) => Promise<void> };
    faceLandmark68TinyNet: { isLoaded: boolean; loadFromUri: (uri: string) => Promise<void> };
    faceRecognitionNet: { isLoaded: boolean; loadFromUri: (uri: string) => Promise<void> };
  };
  TinyFaceDetectorOptions: new (options: Record<string, unknown>) => unknown;
  detectSingleFace: (input: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement, options: unknown) => {
    withFaceLandmarks: (tiny?: boolean) => {
      withFaceDescriptor: () => Promise<FaceApiDetection | undefined>;
    };
  };
};

declare global {
  interface Window {
    faceapi?: FaceApi;
  }
}

export type LocalFaceRecord = {
  faceId: string;
  handle: string;
  descriptor: number[];
  enrolledAt: string;
  storage: "local-device";
  controllerAddress?: string;
  addressVerified?: boolean;
};

export type FaceMatch = {
  record: LocalFaceRecord;
  distance: number;
  confidence: number;
};

const MODEL_URI = "/models";
const REGISTRY_KEY = "face3:local-face-registry:v1";
let modelPromise: Promise<void> | null = null;

export function loadFaceRecognitionModels() {
  if (modelPromise) return modelPromise;
  modelPromise = (async () => {
    const faceapi = window.faceapi;
    if (!faceapi) throw new Error("人脸识别模型尚未加载，请刷新页面重试");
    await Promise.all([
      faceapi.nets.tinyFaceDetector.isLoaded
        ? Promise.resolve()
        : faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URI),
      faceapi.nets.faceLandmark68TinyNet.isLoaded
        ? Promise.resolve()
        : faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URI),
      faceapi.nets.faceRecognitionNet.isLoaded
        ? Promise.resolve()
        : faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URI),
    ]);
  })().catch((error) => {
    modelPromise = null;
    throw error;
  });
  return modelPromise;
}

export async function describeFace(
  input: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement
): Promise<number[] | null> {
  await loadFaceRecognitionModels();
  const faceapi = window.faceapi;
  if (!faceapi) return null;
  const result = await faceapi
    .detectSingleFace(
      input,
      new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.55 })
    )
    .withFaceLandmarks(true)
    .withFaceDescriptor();
  return result?.descriptor ? Array.from(result.descriptor) : null;
}

export function readLocalFaceRegistry(): LocalFaceRecord[] {
  try {
    const value = JSON.parse(localStorage.getItem(REGISTRY_KEY) || "[]");
    if (!Array.isArray(value)) return [];
    return value.filter(
      (item): item is LocalFaceRecord =>
        typeof item?.faceId === "string" &&
        typeof item?.handle === "string" &&
        Array.isArray(item?.descriptor) &&
        item.descriptor.length === 128
    );
  } catch {
    return [];
  }
}

export function saveLocalFaceRecord(record: LocalFaceRecord) {
  const registry = readLocalFaceRegistry().filter((item) => item.faceId !== record.faceId);
  registry.push(record);
  localStorage.setItem(REGISTRY_KEY, JSON.stringify(registry));
}

export function bindLocalFaceAddress(faceId: string, address: string) {
  if (!/^0x[a-fA-F0-9]{40}$/.test(address)) return false;
  const registry = readLocalFaceRegistry();
  const index = registry.findIndex((item) => item.faceId === faceId);
  if (index < 0) return false;
  registry[index] = {
    ...registry[index],
    controllerAddress: address,
    addressVerified: true,
  };
  localStorage.setItem(REGISTRY_KEY, JSON.stringify(registry));
  return true;
}

function euclideanDistance(left: number[], right: number[]) {
  if (left.length !== right.length) return Number.POSITIVE_INFINITY;
  let sum = 0;
  for (let index = 0; index < left.length; index += 1) {
    const delta = left[index] - right[index];
    sum += delta * delta;
  }
  return Math.sqrt(sum);
}

export function resolveLocalFace(descriptor: number[], threshold = 0.52): FaceMatch | null {
  const matches = readLocalFaceRegistry()
    .map((record) => ({ record, distance: euclideanDistance(descriptor, record.descriptor) }))
    .sort((left, right) => left.distance - right.distance);
  const best = matches[0];
  if (!best || best.distance > threshold) return null;
  return {
    ...best,
    confidence: Math.max(0, Math.min(99, Math.round((1 - best.distance / threshold) * 100))),
  };
}
